// Pure helpers for moving tiles on the board. Shared by mouse and touch
// drag-and-drop so both behave identically.

export interface TileLike {
  icon: string;
}
export type Rows<T> = (T | undefined)[][];

const ROW_SIZE = 4;

// Remove gaps so tiles sit left-aligned, padded back to 4 slots
const compact = <T>(row: (T | undefined)[]): (T | undefined)[] => {
  const filled: (T | undefined)[] = row.filter((t) => t !== undefined);
  while (filled.length < ROW_SIZE) filled.push(undefined);
  return filled;
};

export const findTile = <T extends TileLike>(rows: Rows<T>, tile: T): [number, number] | null => {
  for (let r = 0; r < rows.length; r++) {
    const c = rows[r].findIndex((t) => t?.icon === tile.icon);
    if (c !== -1) return [r, c];
  }
  return null;
};

/**
 * Move `tile` (from the pool or another slot) to row `rowIndex`.
 * - tileIndex -1, or an empty slot: appended to the leftmost empty slot.
 * - an occupied slot: swapped with that tile (or replaces it, sending the
 *   replaced tile back to the pool, when the dragged tile came from the pool).
 */
export const moveTile = <T extends TileLike>(
  rows: Rows<T>,
  tile: T,
  rowIndex: number,
  tileIndex: number
): Rows<T> => {
  const next = rows.map((row) => [...row]);
  const from = findTile(next, tile);
  const target = tileIndex === -1 ? undefined : next[rowIndex][tileIndex];

  if (target) {
    if (from) next[from[0]][from[1]] = target;
    next[rowIndex][tileIndex] = tile;
    return next;
  }

  if (from) {
    next[from[0]][from[1]] = undefined;
    next[from[0]] = compact(next[from[0]]);
  }
  const empty = next[rowIndex].findIndex((t) => t === undefined);
  if (empty !== -1) next[rowIndex][empty] = tile;
  return next;
};

/** Send `tile` back to the pool. */
export const removeTile = <T extends TileLike>(rows: Rows<T>, tile: T): Rows<T> => {
  const from = findTile(rows, tile);
  return from ? removeAt(rows, from[0], from[1]) : rows;
};

/** Send the tile at a given slot back to the pool. */
export const removeAt = <T>(rows: Rows<T>, rowIndex: number, tileIndex: number): Rows<T> =>
  rows.map((row, r) => {
    if (r !== rowIndex) return row;
    const copy = [...row];
    copy[tileIndex] = undefined;
    return compact(copy);
  });

/**
 * Which unlocked row (0-3) is under a screen point? Checks the element at the
 * point first, then falls back to row bounds plus `tolerance` pixels.
 */
export const findRowIndexAt = (
  x: number,
  y: number,
  completedRows: boolean[],
  tolerance = 5
): number | null => {
  const rowEl = document.elementFromPoint(x, y)?.closest(".row");
  if (rowEl && rowEl.parentElement && !rowEl.classList.contains("locked")) {
    const i = Array.from(rowEl.parentElement.children).indexOf(rowEl);
    if (i >= 0 && i < 4) return i;
  }
  const rowEls = document.querySelectorAll(".row");
  for (let i = 0; i < rowEls.length; i++) {
    const rect = rowEls[i].getBoundingClientRect();
    if (
      !completedRows[i] &&
      x >= rect.left - tolerance &&
      x <= rect.right + tolerance &&
      y >= rect.top - tolerance &&
      y <= rect.bottom + tolerance
    ) {
      return i;
    }
  }
  return null;
};
